import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useWarehouseContext } from "./WarehouseProvider";
import { useWarehouse } from "./useWarehouse";
import { ReactComponent as CameraSvg } from "./svgs/camera.svg";

import { PhotoConfirmation } from "./components/PhotoConfirmation";
import { StepHeader } from "./components/StepHeader";
import { FullscreenModal } from "./components/FullscreenModal";
import { Webcam } from "./components/Webcam";
import { useSteps } from "./useSteps";
import { RejectedBanner } from "./components/RejectedBanner";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";

const TITLE = "Damaged Packaging Photo";

export const CaptureDamagedPackagingPhotos = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState("");
  const {
    damagedPackagingPictures = [],
    addDamagedPackagingPicture,
    currentTakeback,
  } = useWarehouseContext();
  const { next } = useSteps();
  const webcamRef = useRef(null);
  const takePhotoButtonRef = useRef(null);
  const { setHasCapturedBoxDamageEvidence, requiresBoxDamageEvidence } =
    useWarehouse();

  if (!requiresBoxDamageEvidence) {
    next();
  }

  const handleCapturePackage = useCallback(
    (picture) => {
      addDamagedPackagingPicture(picture);
    },
    [addDamagedPackagingPicture]
  );

  const capturePhoto = useCallback(() => {
    const photo = webcamRef.current?.getScreenshot();
    if (photo) {
      setCapturedPhoto(photo);
      setShowConfirmation(true);
    }
  }, []);

  const handleConfirmPhoto = useCallback(() => {
    const isFirstPhoto = damagedPackagingPictures.length === 0;
    handleCapturePackage(capturedPhoto);
    setShowConfirmation(false);
    setCapturedPhoto("");
    setHasCapturedBoxDamageEvidence(true);
    // Auto-advance to next step after first photo
    if (isFirstPhoto) {
      next();
    }
  }, [
    damagedPackagingPictures.length,
    handleCapturePackage,
    capturedPhoto,
    setHasCapturedBoxDamageEvidence,
    next,
  ]);

  const handleCancelPhoto = useCallback(() => {
    setShowConfirmation(false);
    setCapturedPhoto("");
  }, []);

  // Handle Enter key navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !isLoading) {
        e.preventDefault();

        // If showing confirmation, confirm the photo
        if (showConfirmation) {
          handleConfirmPhoto();
        }
        // If there are no pictures, capture a photo
        else if (damagedPackagingPictures.length === 0) {
          capturePhoto();
        }
        // If there are pictures, capture another photo
        else {
          capturePhoto();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    damagedPackagingPictures.length,
    next,
    isLoading,
    showConfirmation,
    handleConfirmPhoto,
    capturePhoto,
  ]);

  // Get reject category from takeback data
  const takebackData =
    typeof currentTakeback?.data === "object"
      ? currentTakeback?.data
      : JSON.parse(currentTakeback?.data || "{}");
  const rejectCategory = takebackData?.rejectCategory;

  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <Webcam ref={webcamRef} setIsLoading={setIsLoading} />
        <StepHeader opacity="100%" isCameraActive />
        {rejectCategory && (
          <div className="relative z-50">
            <RejectedBanner rejectCategory={rejectCategory} />
          </div>
        )}

        <div className="flex-1 flex flex-col items-center justify-center">
          {/* need this for layout - ideally refactor so its not needed */}
        </div>

        <WarehouseButtonsFooter isCameraActive>
          <WarehouseButtonGroup
            columns={damagedPackagingPictures.length > 0 ? 2 : 1}
          >
            <WarehouseButton
              ref={takePhotoButtonRef}
              variant="primary"
              onClick={capturePhoto}
              disabled={isLoading}
            >
              <div className="flex items-center justify-center gap-2">
                <CameraSvg className="fill-white w-5 h-5" />
                <span>Take Photo of Cardboard Box Damage</span>
              </div>
            </WarehouseButton>
            {damagedPackagingPictures.length > 0 && (
              <WarehouseButton variant="primary" onClick={next}>
                Continue
              </WarehouseButton>
            )}
          </WarehouseButtonGroup>
        </WarehouseButtonsFooter>
      </div>
      {showConfirmation &&
        createPortal(
          <PhotoConfirmation
            title="Photo Cardboard Box Damage"
            photo={capturedPhoto}
            onConfirm={handleConfirmPhoto}
            onCancel={handleCancelPhoto}
            retakeButtonText="Retake Photo of Cardboard Box Damage"
          />,
          document.body
        )}
    </FullscreenModal>
  );
};

CaptureDamagedPackagingPhotos.title = TITLE;
